import React, { useRef, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useComponentConfigClient } from './componentConfig.client';
import { formatDate } from '../date.format';
import { DeleteButton } from '../components/DeleteButton';
import { FlaskConical } from 'lucide-react';
// ─── Constants ─────────────────────────────────────────────────────────────────
const TYPE_TO_INPUT = {
    audio: 'audio',
    camera: 'image',
    file: 'pdf',
    chatbot: 'text',
};
const TYPE_TO_ACCEPT = {
    audio: '.mp3,.wav,.m4a,.ogg,.webm,.aac',
    camera: '.jpg,.jpeg,.png,.webp,.heic',
    file: '.pdf',
    chatbot: '',
};
const TYPE_LABEL = {
    audio: 'Audio file',
    camera: 'Image file',
    file: 'PDF file',
    chatbot: 'Text',
};
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';
// ─── Helpers ───────────────────────────────────────────────────────────────────
function isEnforceSchema(component) {
    return !!component.settings?.enforceSchema;
}
function formatOutputPreview(output) {
    if (typeof output === 'string')
        return output;
    return JSON.stringify(output);
}
const ExamplesTab = ({ component, onComponentUpdated }) => {
    const client = useComponentConfigClient();
    const fileInputRef = useRef(null);
    const inputType = TYPE_TO_INPUT[component.componentType];
    const acceptAttr = TYPE_TO_ACCEPT[component.componentType];
    const isFile = component.componentType !== 'chatbot';
    const enforceSchema = isEnforceSchema(component);
    const [adding, setAdding] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [inputText, setInputText] = useState('');
    const [expectedOutput, setExpectedOutput] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState();
    const resetForm = () => {
        setSelectedFile(null);
        setInputText('');
        setExpectedOutput('');
        setDescription('');
        setFormError(undefined);
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    };
    const handleCancel = () => {
        setAdding(false);
        resetForm();
    };
    const handleAdd = async () => {
        setFormError(undefined);
        // Validate input
        if (isFile && !selectedFile) {
            setFormError(`Please select a ${TYPE_LABEL[component.componentType].toLowerCase()}.`);
            return;
        }
        if (!isFile && !inputText.trim()) {
            setFormError('Input text is required.');
            return;
        }
        // Validate output
        let parsedOutput;
        if (enforceSchema) {
            if (!expectedOutput.trim()) {
                setFormError('Expected output (JSON) is required.');
                return;
            }
            try {
                parsedOutput = JSON.parse(expectedOutput);
            }
            catch {
                setFormError('Expected output must be valid JSON.');
                return;
            }
        }
        else {
            if (!expectedOutput.trim()) {
                setFormError('Expected output is required.');
                return;
            }
            parsedOutput = expectedOutput.trim();
        }
        setSaving(true);
        try {
            let inputUri;
            if (isFile && selectedFile) {
                inputUri = await client.uploadExampleFile(component.componentId, selectedFile);
            }
            const example = await client.addExample(component.componentId, {
                inputType,
                inputText: !isFile ? inputText.trim() : undefined,
                inputUri,
                expectedOutput: parsedOutput,
                description: description.trim() || undefined,
            });
            onComponentUpdated({ ...component, examples: [...component.examples, example] });
            setAdding(false);
            resetForm();
        }
        catch {
            setFormError('Failed to save example. Please try again.');
        }
        finally {
            setSaving(false);
        }
    };
    const handleDelete = async (exampleId) => {
        await client.deleteExample(component.componentId, exampleId);
        onComponentUpdated({
            ...component,
            examples: component.examples.filter(e => e.exampleId !== exampleId),
        });
    };
    return (React.createElement("div", { className: "space-y-6" },
        React.createElement("div", { className: "flex items-start justify-between" },
            React.createElement("div", null,
                React.createElement("h4", { className: "font-semibold text-gray-800 text-base" }, "Fine-tuning Examples"),
                React.createElement("p", { className: "text-sm text-gray-500 mt-0.5" }, "Labeled input/output pairs used to train the agent's processing behavior.")),
            !adding && (React.createElement(Button, { primary: true, size: "small", onClick: () => setAdding(true) },
                React.createElement(Icon, { name: "plus" }),
                " Add Example"))),
        adding && (React.createElement("div", { className: "border border-orange-200 rounded-lg p-5 space-y-4 bg-orange-50" },
            React.createElement("h5", { className: "text-sm font-semibold text-gray-700" }, "New Example"),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass },
                    TYPE_LABEL[component.componentType],
                    " ",
                    React.createElement("span", { className: "text-red-500" }, "*")),
                isFile ? (React.createElement("div", null,
                    React.createElement("input", { ref: fileInputRef, type: "file", accept: acceptAttr, className: "hidden", onChange: e => setSelectedFile(e.target.files?.[0] ?? null) }),
                    React.createElement("div", { className: "flex items-center gap-3" },
                        React.createElement("button", { type: "button", onClick: () => fileInputRef.current?.click(), className: "px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors" },
                            React.createElement(Icon, { name: "upload" }),
                            selectedFile ? 'Change file' : 'Choose file'),
                        selectedFile ? (React.createElement("span", { className: "text-sm text-gray-700 truncate max-w-xs", title: selectedFile.name },
                            selectedFile.name,
                            React.createElement("span", { className: "ml-1 text-gray-400" },
                                "(",
                                Math.round(selectedFile.size / 1024),
                                " KB)"))) : (React.createElement("span", { className: "text-sm text-gray-400" }, "No file chosen"))),
                    React.createElement("p", { className: "text-xs text-gray-400 mt-1" },
                        "Accepted: ",
                        acceptAttr))) : (React.createElement("textarea", { rows: 3, className: inputClass + ' resize-none', placeholder: "The message or query the user sends...", value: inputText, onChange: e => setInputText(e.target.value) }))),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass },
                    "Expected Output",
                    enforceSchema ? ' (JSON)' : '',
                    " ",
                    React.createElement("span", { className: "text-red-500" }, "*"),
                    !enforceSchema && (React.createElement("span", { className: "ml-2 text-xs font-normal text-gray-400" }, "\u2014 plain text (schema enforcement is off)"))),
                React.createElement("textarea", { rows: 5, className: inputClass + ' resize-none' + (enforceSchema ? ' font-mono' : ''), placeholder: enforceSchema ? '{"field": "value"}' : 'The expected text response from the agent...', value: expectedOutput, onChange: e => setExpectedOutput(e.target.value) })),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass },
                    "Description ",
                    React.createElement("span", { className: "text-gray-400 font-normal" }, "(optional)")),
                React.createElement("input", { type: "text", className: inputClass, placeholder: "Short description of this example...", value: description, onChange: e => setDescription(e.target.value) })),
            formError && React.createElement("p", { className: "text-red-500 text-sm" }, formError),
            React.createElement("div", { className: "flex gap-2 justify-end" },
                React.createElement(Button, { size: "small", onClick: handleCancel }, "Cancel"),
                React.createElement(Button, { size: "small", primary: true, loading: saving, onClick: handleAdd }, "Save Example")))),
        component.examples.length > 0 ? (React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg overflow-hidden" },
            React.createElement("div", { className: "grid grid-cols-12 gap-3 px-5 py-3 bg-gray-700 text-white text-sm font-medium" },
                React.createElement("div", { className: "col-span-2" }, "TYPE"),
                React.createElement("div", { className: "col-span-3" }, "INPUT"),
                React.createElement("div", { className: "col-span-4" }, "EXPECTED OUTPUT"),
                React.createElement("div", { className: "col-span-2" }, "CREATED"),
                React.createElement("div", { className: "col-span-1 text-right" }, "DEL")),
            React.createElement("div", { className: "divide-y divide-gray-100" }, component.examples.map(ex => (React.createElement("div", { key: ex.exampleId, className: "grid grid-cols-12 gap-3 px-5 py-3 hover:bg-gray-50" },
                React.createElement("div", { className: "col-span-2 text-sm text-gray-600 capitalize" }, ex.inputType),
                React.createElement("div", { className: "col-span-3 text-sm text-gray-700 truncate", title: ex.inputText || ex.inputUri }, ex.inputText
                    ? ex.inputText
                    : ex.inputUri
                        ? React.createElement("span", { className: "text-gray-400 italic text-xs" }, ex.inputUri.split('/').pop())
                        : '—'),
                React.createElement("div", { className: "col-span-4 text-sm text-gray-700 truncate", title: formatOutputPreview(ex.expectedOutput) }, formatOutputPreview(ex.expectedOutput)),
                React.createElement("div", { className: "col-span-2 text-xs text-gray-400" }, formatDate(ex.createdAt)),
                React.createElement("div", { className: "col-span-1 flex justify-end" },
                    React.createElement(DeleteButton, { iconOnly: true, onConfirm: () => handleDelete(ex.exampleId), title: "Delete Example", message: "Are you sure you want to delete this example?" })))))))) : (!adding && (React.createElement("div", { className: "py-16 text-center text-gray-400" },
            React.createElement(FlaskConical, { className: "w-10 h-10 mx-auto mb-3 opacity-30" }),
            React.createElement("p", { className: "text-lg font-medium text-gray-500" }, "No examples yet"),
            React.createElement("p", { className: "text-sm mt-1" }, "Add labeled examples to guide agent fine-tuning."))))));
};
export default ExamplesTab;
